// useBoothShowcase.js
// Composes the rotating-promo queue for /overlay/booth.
//
// Queue contents:
//   - Curated items from config/boothShowcase.items (active only)
//   - Optional "press logos" synthetic card
//   - Optional ONE pancake slot — content cycles through `pancakePool` every
//     time the rotation lands on it, so each lap around the carousel surfaces
//     a fresh pancake
//
// Pancake pool:
//   - Live shift: all recentlyDone with images (priority)
//   - No live shift: full config/publicGallery, shuffled once (stable order
//     so every pancake gets a turn before any repeats)
//
// Each queue entry has a `kind`: "item" / "press" / "pancake".
// PromoPanel resolves the pancake slot's content at render time by indexing
// into pancakePool with its own cycle counter.

import { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "./../firebase";
import useCurrentShiftOrders from "./useCurrentShiftOrders";
import { activeCombosForShift, fmtCents } from "../utils/combos";
import { shiftPricing, comboPriceCents, comboAlaCarteCents } from "../utils/eventPricing";

const DEFAULT_ROTATION = {
  intervalSec: 12,           // fallback duration when an entry has no explicit durationSec
  pressDurationSec: null,    // null → inherit intervalSec
  pancakeDurationSec: null,  // null → inherit intervalSec
  includePressCard: true,
  // Default OFF — full-resolution photo_cutout_url images accumulated GPU
  // memory over long sessions and made the overlay choppy. Re-enable in
  // Stream Setup if you want to try it again (consider swapping to the 400px
  // cutoutThumbnailUrl source first to keep memory flat).
  includeRecentPancakes: false,
};

// Fisher-Yates shuffle (stable copy, doesn't mutate input)
function shuffle(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return [];
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

function normalizeLivePancake(o) {
  return {
    id: o.id,
    title: o.subjectName || o.recipientName || "Untitled",
    cutoutUrl: o.photo_cutout_url || o.cutoutThumbnailUrl,
    aiDescription: o.ai_description || null,
    artistName: o.artistName || null,
    eventName: o.eventName || null,
    source: "live",
  };
}

function normalizeGalleryPancake(g) {
  return {
    id: g.id,
    title: g.title || "Untitled",
    cutoutUrl: g.cutoutUrl || g.thumbUrl,
    aiDescription: g.aiDescription || null,
    artistName: g.artistName || null,
    eventName: g.eventName || null,
    testimonial: g.testimonial || null,
    source: "gallery",
  };
}

export default function useBoothShowcase() {
  const [doc_, setDocState] = useState(null); // null = loading
  const [galleryItems, setGalleryItems] = useState([]);
  const { recentlyDone, activeShift } = useCurrentShiftOrders();

  useEffect(() => {
    return onSnapshot(
      doc(db, "config", "boothShowcase"),
      (snap) => setDocState(snap.exists() ? snap.data() : { items: [], rotation: {} }),
      () => setDocState({ items: [], rotation: {} })
    );
  }, []);

  // Lazy-load publicGallery; used as the pancake-pool source when there's
  // no active shift. Snapshot listener so a rebuild propagates without reload.
  useEffect(() => {
    return onSnapshot(
      doc(db, "config", "publicGallery"),
      (snap) => {
        if (snap.exists() && Array.isArray(snap.data().items)) {
          setGalleryItems(snap.data().items);
        } else {
          setGalleryItems([]);
        }
      },
      () => setGalleryItems([])
    );
  }, []);

  const rotation = { ...DEFAULT_ROTATION, ...(doc_?.rotation || {}) };
  // HARD OVERRIDE — pancake spotlight is disabled in code while we diagnose
  // OBS compositor lag (full-res cutout cycling caused GPU memory buildup).
  // The toggle in Stream Setup is a no-op until this line is removed. To
  // re-enable: delete this assignment AND swap normalizeGalleryPancake to
  // prefer thumbUrl over cutoutUrl so the rotation uses 400px thumbnails.
  rotation.includeRecentPancakes = false;

  // Pancake pool — held in state so it stays REFERENTIALLY stable across the
  // many Booth.jsx renders (1Hz status-row tick, etc.). Without this, every
  // re-render would call shuffle() with new Math.random(), making the visible
  // pancake change on every tick instead of once per rotation cycle.
  //
  // We rebuild the pool only when the underlying ID set changes — that's the
  // ONLY thing that actually requires a refresh. Reference churn from
  // useCurrentShiftOrders rebuilding its derived arrays is ignored.
  const [pancakePool, setPancakePool] = useState([]);

  const recentlyDoneKey = recentlyDone
    .filter((o) => o.photo_cutout_url || o.cutoutThumbnailUrl)
    .map((o) => o.id)
    .join("|");
  const galleryItemsKey = galleryItems
    .filter((g) => g.cutoutUrl || g.thumbUrl)
    .map((g) => g.id)
    .join("|");

  useEffect(() => {
    const live = recentlyDone
      .filter((o) => o.photo_cutout_url || o.cutoutThumbnailUrl)
      .map(normalizeLivePancake);
    if (live.length > 0) {
      setPancakePool(live);
      return;
    }
    const galleryPool = galleryItems
      .filter((g) => g.cutoutUrl || g.thumbUrl)
      .map(normalizeGalleryPancake);
    setPancakePool(shuffle(galleryPool));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentlyDoneKey, galleryItemsKey]);

  const baseInterval = Math.max(3, rotation.intervalSec ?? 12);
  const pressDur = rotation.pressDurationSec ?? baseInterval;
  const pancakeDur = rotation.pancakeDurationSec ?? baseInterval;

  // Per-shift override: items whose id is in activeShift.disabledShowcaseIds
  // are hidden from the rotation for this shift only. Lets the operator
  // suppress venue-irrelevant spotlight cards (e.g. poffertjes) at one event
  // without globally deactivating them.
  const disabledShowcaseKey = Array.isArray(activeShift?.disabledShowcaseIds)
    ? activeShift.disabledShowcaseIds.join("|")
    : "";
  // Combo spotlight cards — array identity churns per snapshot, so key on a
  // joined string (same trick as disabledShowcaseKey above). ":g" marks
  // goods-only voice (free pancakes, shift.goodsMode "sell") so the memo
  // re-runs when the operator flips the goods toggle mid-shift.
  const activeComboKey = activeCombosForShift(activeShift)
    .map((c) => c.key + (c.goodsOnly ? ":g" : ""))
    .join("|");
  // Road Mode pricing — drives the leading "Today's Menu" card + combo price
  // overrides. Stringified so the memo keys on VALUES, not object identity.
  const pricing = shiftPricing(activeShift);
  const pricingKey = pricing ? JSON.stringify(pricing) : "";

  const queue = useMemo(() => {
    const items = Array.isArray(doc_?.items) ? doc_.items : [];
    const disabledSet = new Set(activeShift?.disabledShowcaseIds || []);
    const out = [];

    // Road Mode menu card LEADS the rotation — derived entirely from the
    // shift's pricing block so the screen can never disagree with checkout.
    if (pricing && (pricing.flashCents || pricing.customCents)) {
      const rows = [];
      if (pricing.flashCents) rows.push({ icon: "⚡", name: "Flash pancake", price: fmtCents(pricing.flashCents) });
      if (pricing.customCents) rows.push({ icon: "📷", name: "Your photo → pancake", price: fmtCents(pricing.customCents) });
      pricing.extras.forEach((e) => rows.push({ icon: e.emoji, name: e.label, price: `+${fmtCents(e.cents)}` }));
      out.push({
        kind: "menu",
        id: "road_menu",
        menu: {
          rows,
          note: pricing.menuNote,
          taxLine: pricing.taxPct ? `+ ${pricing.taxPct}% ${pricing.taxLabel.toLowerCase()}` : null,
        },
        durationSec: Math.max(3, baseInterval + 4),
      });
    }

    items
      .filter((it) => it && it.active !== false && it.imageUrl && !disabledSet.has(it.id))
      .forEach((it) => out.push({
        kind: "item",
        id: `item_${it.id}`,
        item: it,
        durationSec: Math.max(3, it.durationSec ?? baseInterval),
      }));

    if (rotation.includePressCard) {
      out.push({ kind: "press", id: "press", durationSec: Math.max(3, pressDur) });
    }

    // Combo upsell cards — driven by shift.activeComboKeys (Event Config /
    // Shift Controls). activeCombosForShift already suppresses party voice,
    // so client-paid events never rotate commerce.
    activeComboKey.split("|").filter(Boolean).forEach((tag) => {
      const [key, g] = tag.split(":");
      const goodsOnly = g === "g";
      out.push({
        kind: "combo", id: `combo_${tag}`, comboKey: key, goodsOnly,
        // Road Mode market-voice overrides (goods voice keeps static deltas).
        priceCents: pricing && !goodsOnly ? comboPriceCents(activeShift, key) : undefined,
        compareCents: pricing && !goodsOnly ? comboAlaCarteCents(activeShift, key) : undefined,
        durationSec: Math.max(3, baseInterval),
      });
    });

    // Single pancake slot (content cycles each visit, resolved by PromoPanel)
    if (rotation.includeRecentPancakes && pancakePool.length > 0) {
      out.push({ kind: "pancake", id: "pancake_slot", durationSec: Math.max(3, pancakeDur) });
    }

    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc_, rotation.includePressCard, rotation.includeRecentPancakes, pancakePool.length, baseInterval, pressDur, pancakeDur, disabledShowcaseKey, activeComboKey, pricingKey]);

  return {
    queue,
    pancakePool,
    loading: doc_ === null,
  };
}
